(ns admin.components.composite.moment-editor.edit.details.question
  (:require [re-frame.core :as re-frame]
            [reagent.core :as reagent]

            [kyber.components.base.button :as button]
            [kyber.components.base.icons :as icons]
            [kyber.components.base.text :as text]
            [kyber.styles.core :refer [gs styles->classes]]

            [admin.components.composite.moment-editor.edit.details.question.single-answer
             :as single-answer-question]
            [admin.components.composite.moment-editor.edit.details.question.multiple-answer
             :as multiple-answer-question]
            [admin.components.composite.moment-editor.edit.details.question.free-response
             :as free-response-question]
            
            [admin.components.composite.moment-editor.edit.details.question.switch-question-type-button
             :as switch-question-type-button]

            [admin.events.questions :as >questions]
            [admin.events.moments :as >moments]))

(def classes
  (styles->classes
   {:container
    {:padding [(gs [:spacing :p8])
               (gs [:spacing :p20])
               (gs [:spacing :p20])]}}))

(def question-types
  {:single-answer {:question/type :single-answer
                   :title "Single Answer"
                   :icon  :check-circle}
   :multiple-answer {:question/type :multiple-answer
                     :title "Multiple Answer"
                     :icon  :list-square}
   :free-response {:question/type :free-response
                   :title "Free Response"
                   :icon  :comment}})

(def graded-questions
  [(assoc (:single-answer question-types)
          :label "Learner must choose a single correct answer.")
   (assoc (:multiple-answer question-types)
          :label "Learner must choose all the correct choices.")])

(def ungraded-questions
  [(assoc (:single-answer question-types)
          :label "Learner must choose a single answer option.")
   (assoc (:multiple-answer question-types)
          :label "Learner can choose more than one answer option.")
   (assoc (:free-response question-types)
          :label "Learner provides a response in the form of free form text.")])

(defn- question-selector-button
  [{:keys [question props has-correctness]
    :or {has-correctness false}}]
  (let [{:keys [dispatch-local
                is-updating]}
        props

        hovering?
        (reagent/atom false)

        swap-hover
        #(swap! hovering? not)]
    (fn []
      [button/secondary-button
       {:style {:width "100%"
                :height :auto
                :margin-bottom (gs [:spacing :p12])}
        :disabled @is-updating
        :on-mouse-over swap-hover
        :on-mouse-out swap-hover
        :on-click #(dispatch-local [:set-new-question {:question/type (:question/type question)
                                                       :question/has-correctness has-correctness}])}
       [:div
        [:div
         [icons/icon {:style {:margin-right (gs [:spacing :p12])}} (:icon question)]
         [text/caption-10 :span (:title question)]]
        [text/caption-10 :p (cond-> {:style {:margin-bottom (gs [:spacing :p0])}}
                              (true? @hovering?)
                              (assoc-in [:style :color]
                                        (gs [:colors :true-white])))
         (:label question)]]])))

(defn- question-selector [props question-deleted?]
  [:<>
   (when (seq graded-questions)
     [:div {:style {:margin-top (gs [:spacing :p12])}}
      (when question-deleted?
        [text/caption-30 :p {:style {:color (gs [:colors :error-500])}}
         "You have not selected your new question type. Select a question type or lose your work."])
      
      [text/caption-30 :p "Add a Graded Question"]
      [text/caption-10 :p "Graded questions have a correct answer."]
      [:div
       (for [question graded-questions]
         ^{:key question}
         [question-selector-button {:question question
                                    :props props
                                    :has-correctness true}])]])
   (when (seq ungraded-questions)
     [:div {:style {:margin-top (gs [:spacing :p12])}}
      [text/caption-30 :p "Add an Ungraded Question"]
      [text/caption-10 :p
       (str "Ungraded questions do not have a correct answer and will "
            "not count toward the learner's overall test score.  ")]
      [:div
       (for [question ungraded-questions]
         ^{:key question}
         [question-selector-button {:question question
                                    :props props}])]])])

(defn render [{:keys [moment editor-state dispatch-local] :as props}]
  (let [{:keys [new-question
                activity-uuid
                curriculum-uuid
                question-deleted?]}
        @editor-state

        moment-uuid (@moment :moment/uuid)

        activity
        (re-frame/subscribe [:activities/activity
                             {:curriculum-uuid curriculum-uuid
                              :activity-uuid activity-uuid}])
        {:keys [allow-disabling-of-cbqs]
         :or {allow-disabling-of-cbqs false}}
        @activity

        question-type
        (keyword (or (-> @moment :moment/question :question/type)
                     (:question/type new-question)))

        question-uuid (-> @moment :moment/question :question/uuid)

        update-moment-event
        ::>moments/update-moment

        are-updating
        (re-frame/subscribe
         [:request/any-dispatching
          update-moment-event
          ::>questions/update-question
          ::>questions/create-question])

        on-submit
        (fn [submission]
          (let [{question-body :question/body
                 question-choices :question/choices
                 question-type :question/type
                 question-uuid :question/uuid
                 educator-feedback :question/educator-feedback
                 moment-confidence :confidence
                 include-poll-data :include-poll-data}
                submission
                question-event-name
                (if (some? question-uuid)
                  ::>questions/update-question
                  ::>questions/create-question)

                include-confidence? #(true? (:question/has-correctness %))]
            (re-frame/dispatch
             [question-event-name
              (cond-> {:activity/uuid activity-uuid
                       :curriculum/uuid curriculum-uuid
                       :question/type (name question-type)
                       :question/body question-body
                       :question/educator-feedback educator-feedback}
                (some? question-uuid)
                (assoc :question/uuid question-uuid)

                (not= (name question-type) "free-response")
                (assoc :question/choices question-choices))
              {:response-keys (cond->
                               [:question/uuid
                                :question/type
                                :question/body
                                {:question/choices [:question.choice/body
                                                    :question.choice/correct-answer
                                                    :question.choice/uuid]}
                                :question/created-at
                                :question/has-correctness
                                :question/educator-feedback])
               :on-success-toast (if (nil? question-uuid)
                                   "Question created."
                                   "Question updated.")
               :on-failure-toast (if (nil? question-uuid)
                                   "Something went wrong creating this question."
                                   "Something went wrong updating this question.")
               :on-success (fn [question]
                             (dispatch-local [:update-question-deleted? false])
                             (re-frame/dispatch
                              [update-moment-event
                               (cond-> {:moment/activity #:activity{:uuid activity-uuid}
                                        :moment/curriculum #:curriculum{:uuid curriculum-uuid}
                                        :moment/question #:question{:uuid (:question/uuid question)}
                                        :moment/uuid (:moment/uuid @moment)}

                                 (include-confidence? question)
                                 (assoc :moment/confidence
                                        (if (some? moment-confidence)
                                          moment-confidence
                                          true))

                                 (some? include-poll-data)
                                 (assoc :moment/include-poll-data include-poll-data))
                               {:on-success #(do
                                              ;;  set-form-fields with nils ensures a newly saved question renders as saved
                                              ;;  rather than how it exists in editor-state.  This ensures empty fields
                                              ;;  or extra correct answers do not display.
                                               (dispatch-local [:set-form-fields
                                                                {:question-text nil
                                                                 :answer-options nil
                                                                 :show-results nil
                                                                 :add-copy nil
                                                                 :free-text-response nil}])
                                               (when (nil? question-uuid)
                                                 (dispatch-local [:unset-new-question]))
                                               (dispatch-local [:set-dirty false])
                                               (re-frame/dispatch
                                                [:notifier/create-toast
                                                 {:message (str "Question "
                                                                (if (nil? question-uuid)
                                                                  "created"
                                                                  "updated")
                                                                "!")}]))
                                :plo-type (:plo-type @editor-state)}
                               (cond-> [:moment/uuid
                                        :moment/question
                                        :moment/valid
                                        :moment/admin-message
                                        :moment/required
                                        :moment/type
                                        :moment/behavior
                                        :moment/aggregate-results
                                        :moment/include-poll-data]

                                 (include-confidence? question) (conj :moment/confidence))]))}])))

        enriched-props
        (assoc props
               :is-updating are-updating
               :on-submit on-submit
               :allow-disabling-of-cbqs allow-disabling-of-cbqs)]
    
    [:div {:class (:container classes)}
     [switch-question-type-button/render {:editor-state editor-state
                                          :moment moment
                                          :question-uuid question-uuid
                                          :moment-uuid moment-uuid
                                          :activity-uuid activity-uuid
                                          :curriculum-uuid curriculum-uuid
                                          :dispatch-local dispatch-local}]
     
     (when (and question-deleted? (not (nil? question-type)))
       [text/caption-30 :p {:style {:color (gs [:colors :error-500])}}
        "You have not saved your question. Scroll down and click the blue save button or lose your work."])
     
     (case question-type
       :single-answer
       [single-answer-question/render enriched-props]

       :multiple-answer
       [multiple-answer-question/render enriched-props]

       :free-response
       [free-response-question/render enriched-props]

       [question-selector enriched-props question-deleted?])]))
